// Pure derivations over the PRO topic tree used by the form cascade. Kept
// stateless so they can be memoized at the call site without import-cycle
// concerns. Mirrors the prototype helpers `getCategoriesFromTopics` /
// `getSubAreasByCategory` / `getItemsByCategorySubArea` in
// prototype/admin/pages/management-guidelines.html.
//
// SHCT-639 — `treeToPidRefs` / `getPidsByArea` are gone with the PID auto-link
// field (Requirement Clarification No.45, PRD 07 v1.7). Nothing derives PIDs here
// any more; the guideline ↔ PRO link is the 영역/하위영역/항목 + Grade tuple.

import type { ProDomain } from '@/features/pro-item-design/types/pro-item-design.types';

import type {
  ProTopicNode,
  ProTree,
} from '../types/management-guidelines.types';

// Map the PRO Item Design feature's tree (ProDomain[]) into the management-
// guidelines ProTree wire shape. The two features model the same 3-depth tree
// with different field names (subAreas/order vs subDomains/sortOrder); this is
// the single adapter between them so both the mock and the real HTTP adapter
// derive the cascade + PID chips from one source of truth (PRD 04).
export function domainsToProTree(domains: ProDomain[]): ProTree {
  return {
    domains: domains.map((domain) => ({
      id: domain.id,
      name: domain.name,
      sortOrder: domain.order,
      subDomains: domain.subAreas.map((subArea) => ({
        id: subArea.id,
        name: subArea.name,
        sortOrder: subArea.order,
        items: subArea.items.map((item) => ({
          id: item.id,
          pid: item.pid,
          name: item.name,
          status: item.status,
          subDomainId: subArea.id,
        })),
      })),
    })),
  };
}

// Flatten the nested PRO tree (GET /admin/pro/tree) into the cascade view-model:
// one node per (domain, sub-domain) pair. Items carry name only — the cascade
// keys off names. This is the single-source derivation for the Area/Sub-area/
// Item select.
export function treeToTopicNodes(tree: ProTree): ProTopicNode[] {
  return tree.domains.flatMap((domain) =>
    domain.subDomains.map((sub) => ({
      id: sub.id,
      category: domain.name,
      subArea: sub.name,
      items: sub.items.map((item) => ({ name: item.name })),
    })),
  );
}

export function getCategories(topics: ProTopicNode[]): string[] {
  return Array.from(new Set(topics.map((t) => t.category).filter(Boolean)));
}

export function getSubAreas(topics: ProTopicNode[], category: string): string[] {
  if (!category) return [];
  return Array.from(
    new Set(
      topics.filter((t) => t.category === category).map((t) => t.subArea).filter(Boolean),
    ),
  );
}

export function getItems(
  topics: ProTopicNode[],
  category: string,
  subArea: string,
): string[] {
  if (!category || !subArea) return [];
  const names = topics
    .filter((t) => t.category === category && t.subArea === subArea)
    .flatMap((t) => t.items.map((item) => item.name).filter(Boolean));
  return Array.from(new Set(names));
}
